/**
 * EvidenciasSection — evidência por requisito na Conferência (Contract-Driven, Onda 5 —
 * SPEC-ONDA-5 FR-7, FR-8).
 *
 * Estado da entrega (selo) com os motivos por repositório, a cobertura (requisitos
 * provados de N, problemas por fase) e a tabela requisito × implementação × testes ×
 * runtime, filtrável por estado. Os checks do PR no GitHub do cliente aparecem no fim.
 *
 * `assurance` null/undefined → não renderiza nada.
 */
import { useMemo, useState } from 'react';
import { C } from '@/theme/palette';
import { SeloDeGarantia } from '@/components/SeloDeGarantia';
import { useExpandirTudo } from '@/components/ExportacaoContext';

export const ESTADOS = [['todos', 'Todos'], ['falhou', 'Falharam'], ['parcial', 'Parciais'],
                 ['sem_prova', 'Sem prova'], ['provado', 'Provados']];
export const ROTULO_ESTADO = { provado: 'provado', parcial: 'parcial', falhou: 'falhou',
                        sem_prova: 'sem prova' };
const COR_ESTADO = { provado: C.ok, parcial: C.warn, falhou: C.bad, sem_prova: C.neutralMuted };
export const ICONE = { passou: '✅', falhou: '❌', parcial: '🟡', nao_conferido: '⚪' };
export const ROTULO_EIXO = { passou: 'passou', falhou: 'falhou', parcial: 'parcial',
                      nao_conferido: 'não conferido' };

// A URL do check vem de terceiros (o `target_url` de um status): só http(s) vira link (A8).
export function urlSegura(url) {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch {
    return null;
  }
}

function Eixo({ eixo }) {
  if (!eixo?.status) return <td style={{ padding: '6px 8px', color: C.neutralMuted }}>—</td>;
  return (
    <td style={{ padding: '6px 8px', verticalAlign: 'top' }}
        title={(eixo.evidencia ?? []).join('\n')}>
      <span style={{ fontSize: 12 }}>{ICONE[eixo.status] ?? ''} {ROTULO_EIXO[eixo.status] ?? eixo.status}</span>
    </td>
  );
}

export function EvidenciasSection({ assurance }) {
  const [escolhaFiltro, setFiltro] = useState('todos');
  // Exportação: todos os requisitos (a escolha do usuário fica guardada).
  const expandirTudo = useExpandirTudo();
  const filtro = expandirTudo ? 'todos' : escolhaFiltro;
  const requisitos = useMemo(
    () => (assurance?.requisitos ?? []).filter((r) => filtro === 'todos' || r.estado === filtro),
    [assurance, filtro],
  );
  if (!assurance) return null;
  const repos = assurance.repos ?? [];
  const checks = assurance.checks ?? [];
  // um requisito por código (a API junta os repositórios): somar as métricas de cada
  // repositório contaria o mesmo RF duas vezes no multi-repo
  const todos = assurance.requisitos ?? [];
  const total = todos.length;
  const provados = todos.filter((r) => r.estado === 'provado').length;

  return (
    <section aria-label="Evidências por requisito" style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: C.neutral, margin: 0 }}>
          Evidências por requisito
        </h2>
        <SeloDeGarantia estado={assurance.estado} />
      </div>
      <p style={{ fontSize: 12, color: C.neutralDim, margin: '0 0 10px' }}>
        {provados.toLocaleString('pt-BR')} de {total.toLocaleString('pt-BR')} requisito(s)
        provado(s) — implementação, testes e runtime. O mesmo conteúdo sai em
        reports/software-assurance-report.md na entrega.
      </p>
      {repos.map((r) => (
        <div key={r.repo || 'repo'} style={{ fontSize: 12, color: C.neutralMuted, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {r.repo && <span style={{ fontFamily: 'monospace' }}>{r.repo}</span>}
            <SeloDeGarantia estado={r.estado} compacto />
            {r.metricas && (
              <span style={{ color: C.neutralDim }}>
                problemas — antes da geração: {r.metricas.pre ?? 0} · na geração: {r.metricas.geracao ?? 0} ·
                no runtime: {r.metricas.runtime ?? 0}
              </span>
            )}
          </div>
          {(r.motivos ?? []).length > 0 && (
            <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
              {r.motivos.map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '10px 0' }}>
        {ESTADOS.map(([k, rotulo]) => (
          <button key={k} type="button" aria-pressed={filtro === k} onClick={() => setFiltro(k)}
                  data-export="estatico"
                  style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                           backgroundColor: filtro === k ? `${C.cyber}22` : C.blackElev,
                           border: `1px solid ${filtro === k ? C.cyber : C.blackBorder}`,
                           color: filtro === k ? C.cyber : C.neutralDim }}>
            {rotulo}
          </button>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              {['Requisito', 'Estado', 'Implementação', 'Testes', 'Runtime'].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: C.neutralDim }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {requisitos.map((r) => (
              <tr key={`${r.repo}:${r.codigo}`} style={{ borderTop: `1px solid ${C.blackBorder}` }}>
                <td style={{ padding: '6px 8px', color: C.neutral }}>
                  <span style={{ fontFamily: 'monospace', color: C.cyber }}>{r.codigo}</span> {r.titulo}
                </td>
                <td style={{ padding: '6px 8px', color: COR_ESTADO[r.estado] ?? C.neutralDim, fontWeight: 700 }}>
                  {ROTULO_ESTADO[r.estado] ?? r.estado}
                </td>
                <Eixo eixo={r.implementacao} />
                <Eixo eixo={r.testes} />
                <Eixo eixo={r.runtime} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {checks.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: C.neutralMuted, margin: '0 0 6px' }}>
            CI do cliente (checks do PR)
          </h3>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
            {checks.map((k) => (
              <li key={`${k.repo}:${k.nome}`} style={{ fontSize: 12, color: C.neutralMuted }}>
                {iconeDoCheck(k)}{' '}
                {urlSegura(k.url) ? <a href={urlSegura(k.url)} target="_blank" rel="noopener noreferrer"
                            style={{ color: C.cyber }}>{k.nome}</a> : k.nome}
                {' '}— {rotuloDoCheck(k)}{k.resumo ? ` · ${k.resumo}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// Check concluído sem conclusão é registro da plataforma (sem CI, não conferido, espera):
// não está "em andamento". `neutral`/`skipped` não são reprovação.
const _NAO_REPROVA = new Set(['success', 'neutral', 'skipped']);

export function iconeDoCheck(k) {
  if (k.conclusao === 'success') return '✅';
  if (k.conclusao === 'neutral' || k.conclusao === 'skipped') return '➖';
  if (k.conclusao) return '❌';
  return k.status === 'completed' ? '⚪' : '⏳';
}

export function rotuloDoCheck(k) {
  if (k.conclusao) return _NAO_REPROVA.has(k.conclusao) ? k.conclusao : `${k.conclusao} (reprovou)`;
  return k.status === 'completed' ? 'não conferido' : (k.status || 'em andamento');
}

export default EvidenciasSection;
